// Leaflet map setup shared by the Photos/Map screen and the Reports "Photo locations"
// card. Uses marker clustering so the map stays usable as photo count grows
// (spec §7.1 "Map performance").
//
// Leaflet + the clustering plugin (~150KB together) are loaded lazily on first use
// rather than bundled into the app shell, so the Dashboard's Quick Add flow — the
// build brief's #1 priority — stays on the small fast-loading critical path.

import type * as LeafletNS from 'leaflet';
import { addTileLayer } from './tiles';
import { thumbURL } from './photoUrls';
import type { Photo } from '../types';

let leafletPromise: Promise<typeof LeafletNS> | null = null;
function loadLeaflet(): Promise<typeof LeafletNS> {
  if (!leafletPromise) {
    leafletPromise = import('leaflet').then(async (mod) => {
      const L = ((mod as { default?: typeof LeafletNS }).default ?? mod) as typeof LeafletNS;
      await import('leaflet.markercluster'); // side-effect: attaches L.markerClusterGroup
      return L;
    });
  }
  return leafletPromise;
}

const instances = new Map<string, LeafletNS.Map>();

export function destroyMap(elId: string): void {
  const m = instances.get(elId);
  if (m) {
    m.remove();
    instances.delete(elId);
  }
}

export async function initLeaflet(elId: string, photos: Photo[], opts: { isReport?: boolean } = {}): Promise<void> {
  const L = await loadLeaflet();
  const el = document.getElementById(elId);
  if (!el) return;
  destroyMap(elId);

  const located = photos.filter((p): p is Photo & { lat: number; lng: number } => p.lat != null && p.lng != null);
  const map = L.map(elId, { zoomControl: !opts.isReport, attributionControl: true }).setView(
    located.length ? [located[0].lat, located[0].lng] : [54, 15],
    opts.isReport ? 3 : 4
  );
  addTileLayer(L, map);
  instances.set(elId, map);

  const clusterGroup = L.markerClusterGroup({
    maxClusterRadius: 50,
    iconCreateFunction(cluster) {
      const count = cluster.getChildCount();
      const size = 32 + Math.min(count, 12) * 2;
      return L.divIcon({
        html: `<div class="map-cluster" style="width:${size}px;height:${size}px;">${count}</div>`,
        className: '',
        iconSize: L.point(size, size),
      });
    },
  });

  for (const p of located) {
    const marker = L.circleMarker([p.lat, p.lng], {
      radius: 8,
      color: '#39d97a',
      weight: 2,
      fillColor: '#39d97a',
      fillOpacity: 0.55,
    });
    const popupId = `popup-${p.id}`;
    marker.bindPopup(`<div id="${popupId}" style="width:180px;min-height:20px;"></div>`);
    marker.on('popupopen', () => {
      thumbURL(p.id).then((src) => {
        const target = document.getElementById(popupId);
        if (!target) return;
        target.innerHTML = `${src ? `<img class="popup-photo" src="${src}" alt=""/>` : ''}<div class="popup-date">${
          p.place || 'No location label'
        } · ${p.capturedAt.replace('T', ' ')}</div>`;
      });
    });
    clusterGroup.addLayer(marker);
  }
  map.addLayer(clusterGroup);

  if (located.length > 1) {
    map.fitBounds(clusterGroup.getBounds().pad(0.3));
  }
}
